import { useState, useEffect } from 'react';
import { doc, updateDoc, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { formatDateToKorean } from '../../utils';
import { AI_API_URL } from '../../constants';

export const useHistoryManager = (userId, isStreaming, selectedIdx, setSelectedIdx, setAnalyzedVideoId, setAnalyzedTitle, setAnalyzedUrl) => {
  // ✅ 히스토리 관련 상태
  const [history, setHistory] = useState([]);
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // ✅ 히스토리 수정 함수
  const handleEditHistory = async (id, customTitle) => {
    if (!customTitle || typeof customTitle !== 'string' || !customTitle.trim()) {
      alert('제목을 입력해 주세요.');
      return;
    }
    try {
      const videoDocRef = doc(db, "users", userId, "history", id);
      await updateDoc(videoDocRef, {
        customTitle: customTitle,
        lastUpdated: formatDateToKorean(new Date())
      });
      setHistory(history.map(item =>
        item.id === id ? { ...item, customTitle } : item
      ));
      const idx = history.findIndex(item => item.id === id);
      if (idx !== -1) setSelectedIdx(idx);
      setEditingHistoryId(null);
      setEditingTitle("");
    } catch (e) {
      alert("히스토리 수정 중 오류: " + e.message);
    }
  };

  // ✅ 히스토리 삭제 시작 함수
  const handleDeleteClick = (id, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
  };

  // ✅ 히스토리 삭제 확인 함수
  const handleDeleteConfirm = async () => {
    if (!deleteTargetId) return;

    try {
      // 백엔드 API로 완전 삭제
                  const response = await fetch(`${AI_API_URL}/api/delete-history/${deleteTargetId}`, {
          method: 'DELETE',
          headers: { 'user-id': userId }
        });
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('✅ 히스토리 완전 삭제 성공');
        
        // 삭제된 항목이 현재 선택된 항목이면 첫 번째 항목으로 변경
        if (history[selectedIdx]?.id === deleteTargetId) {
          setSelectedIdx(0);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ 히스토리 삭제 실패:', response.status, errorText);
        
        // 사용자에게 에러 메시지 표시
        alert(`히스토리 삭제 실패: ${response.status} ${response.statusText}\n${errorText}`);
      }
    } catch (error) {
      console.error("❌ 히스토리 삭제 네트워크 에러:", error.message);
      alert(`히스토리 삭제 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setDeleteDialogOpen(false);
      setDeleteTargetId(null);
    }
  };

  // ✅ 실시간 히스토리 동기화 (Firebase 리스너)
  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db, "users", userId, "history"),
      where("status", "in", ["active", "analyzing", "stopped", "extracting", "processing", "error"]),
      orderBy("lastUpdated", "desc")
    );
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const loaded = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
          loaded.push({ ...data, id: doc.id });
      });
      setHistory(loaded);
      if (!isStreaming && loaded.length > 0 && selectedIdx === -1) {
        setSelectedIdx(0);
      }
    }, (error) => {
      console.error("히스토리 실시간 로드 중 에러:", error);
    });
    return () => unsubscribe();
  }, [userId, isStreaming, selectedIdx, setSelectedIdx]);

  // ✅ 선택된 히스토리 정보 동기화
  useEffect(() => {
    if (!isStreaming && history.length > 0 && typeof selectedIdx === 'number' && selectedIdx >= 0 && selectedIdx < history.length) {
      const selectedItem = history[selectedIdx];
      
      setAnalyzedVideoId(selectedItem.videoId || '');
      setAnalyzedTitle(selectedItem.customTitle || selectedItem.title || '');
      setAnalyzedUrl(selectedItem.url || '');
    }
  }, [selectedIdx, history, isStreaming, setAnalyzedVideoId, setAnalyzedTitle, setAnalyzedUrl]);

  return {
    // 상태
    history,
    setHistory,
    editingHistoryId,
    setEditingHistoryId,
    editingTitle,
    setEditingTitle,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deleteTargetId,
    setDeleteTargetId,
    
    // 함수
    handleEditHistory,
    handleDeleteClick,
    handleDeleteConfirm,
  };
}; 